/**
 * Retry utility with exponential backoff
 *
 * Provides automatic retry logic with exponential backoff for handling transient errors.
 * Useful for network requests, database queries, and other operations that may fail temporarily.
 */

import logger from 'src/utils/logger';

export interface RetryOptions {
  /**
   * Maximum number of retry attempts (including the initial attempt)
   * Default: 3
   */
  maxAttempts: number;

  /**
   * Initial delay in milliseconds before the first retry
   * Default: 1000ms (1 second)
   */
  initialDelay: number;

  /**
   * Maximum delay in milliseconds between retries
   * Default: 5000ms (5 seconds)
   */
  maxDelay: number;

  /**
   * Multiplier for exponential backoff
   * delay = initialDelay * (backoffMultiplier ^ attemptNumber)
   * Default: 2
   */
  backoffMultiplier: number;

  /**
   * Optional function to determine if an error should trigger a retry
   * Return true to retry, false to throw immediately
   * Default: () => true (retry all errors)
   */
  shouldRetry?: (error: any) => boolean;
}

/**
 * Execute a function with automatic retry and exponential backoff
 *
 * @param fn - The async function to execute with retry logic
 * @param options - Retry configuration options
 * @returns Promise resolving to the function's result
 * @throws The last error if all retry attempts fail
 *
 * @example
 * ```typescript
 * // Retry API call on 5xx errors
 * const result = await retryWithBackoff(
 *   () => userApiService.getUserAcls(),
 *   {
 *     maxAttempts: 3,
 *     initialDelay: 1000,
 *     maxDelay: 5000,
 *     backoffMultiplier: 2,
 *     shouldRetry: (error) => {
 *       const status = error?.response?.status;
 *       return status >= 500 && status < 600;
 *     }
 *   }
 * );
 * ```
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions
): Promise<T> {
  const {
    maxAttempts,
    initialDelay,
    maxDelay,
    backoffMultiplier,
    shouldRetry = () => true,
  } = options;

  let lastError: any;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      logger.info(`[Retry] Attempt ${attempt}/${maxAttempts}`);
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry this error
      if (!shouldRetry(error)) {
        logger.warn(
          `[Retry] Error not retryable, throwing immediately`,
          error
        );
        throw error;
      }

      // If this was the last attempt, throw the error
      if (attempt === maxAttempts) {
        logger.error(
          `[Retry] All ${maxAttempts} attempts failed, throwing error`,
          error
        );
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        initialDelay * Math.pow(backoffMultiplier, attempt - 1),
        maxDelay
      );

      logger.warn(
        `[Retry] Attempt ${attempt}/${maxAttempts} failed, retrying in ${delay}ms`,
        error
      );

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError;
}

/**
 * Predefined retry configurations for common use cases
 */
export const RetryPresets = {
  /**
   * Default retry configuration
   * 3 attempts, 1s initial delay, 5s max delay, 2x backoff
   */
  default: {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
  } as RetryOptions,

  /**
   * Fast retry for quick operations
   * 3 attempts, 500ms initial delay, 2s max delay, 2x backoff
   */
  fast: {
    maxAttempts: 3,
    initialDelay: 500,
    maxDelay: 2000,
    backoffMultiplier: 2,
  } as RetryOptions,

  /**
   * Aggressive retry for critical operations
   * 5 attempts, 2s initial delay, 10s max delay, 2x backoff
   */
  aggressive: {
    maxAttempts: 5,
    initialDelay: 2000,
    maxDelay: 10000,
    backoffMultiplier: 2,
  } as RetryOptions,

  /**
   * Retry only on 5xx server errors (transient errors)
   * 3 attempts, 1s initial delay, 5s max delay, 2x backoff
   */
  serverErrors: {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
    shouldRetry: (error: any) => {
      const status = error?.response?.status;
      return status >= 500 && status < 600;
    },
  } as RetryOptions,
};
